import { FeatureFlag, Site } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { TrackedTranslation } from '@swf/core/types/i18n';

import Button from '../../../../../../Button/Button';
import CountrySelect, { findCountry } from '../../../../../../CountrySelect/CountrySelect';
import SignupStateSelect, {
  getCountryStates
} from '../../../../SignupStateSelect/SignupStateSelect';

import { TXTCLI_Change, TXTWCL_Find_my_country } from './SignupCountryField.messages.json';
import { changeText, selected, withBorder, withoutBorder } from './SignupCountryField.scss';

const getCountryValue = (
  detectedCountry: string,
  userSelectedCountry?: string
): string | undefined => {
  const ignorePreselect = ['NL'];

  if (userSelectedCountry) {
    return userSelectedCountry;
  }

  if (ignorePreselect.includes(detectedCountry)) {
    return;
  }

  return detectedCountry;
};

const getRegionValue = (
  detectedRegion?: string,
  userSelectedRegion?: string
): string | undefined => {
  if (userSelectedRegion) {
    return userSelectedRegion;
  }
  return detectedRegion;
};

export const findUserState = (lookupValue: string, states: { value: string; text: string }[]) =>
  states.find(({ value }) => value === lookupValue);

interface Props {
  showBorders: boolean;
  submitOnSelectionChange: boolean;
  onCountryChange?: (country: { value: string; text: string }) => void;
  onRegionChange?: (region: { value: string; text: string } | undefined) => void;
}

const SignupCountryField: React.FC<Props> = ({
  showBorders,
  submitOnSelectionChange,
  onCountryChange,
  onRegionChange
}) => {
  const form = useForm();
  const detectedCountry = useSelector(select.country.getCountry);
  const detectedRegion = useSelector(select.country.getState);
  const site = useSelector(select.cms.getSiteId);
  const userSelectedCountry = form.getState().values.country;
  const userSelectedRegion = form.getState().values.state;
  const [country, setCountry] = useState<{ value: string; text: string }>();
  const [label, setLabel] = useState<TrackedTranslation>(TXTWCL_Find_my_country);
  const [region, setRegion] = useState<{ value: string; text: string }>();
  const isAutofocusEnabled = useFeature(FeatureFlag.SwcSignupRmfAutofocus);
  const isAvailableCountryForRegion =
    userSelectedCountry === Country.Canada && (site === Site.COM || site === Site.CAON);

  useEffect(() => {
    const countryToSet = findCountry(getCountryValue(detectedCountry, userSelectedCountry));
    setCountry(countryToSet);
    if (onCountryChange) {
      onCountryChange(countryToSet);
    }
    const regionName = getRegionValue(detectedRegion, userSelectedRegion);
    if (country && regionName) {
      const regionToSet = findUserState(regionName, getCountryStates(country.value));
      setRegion(regionToSet);

      if (onRegionChange) {
        onRegionChange(regionToSet);
      }
    }

    setLabel(country ? TXTCLI_Change : TXTWCL_Find_my_country);
  }, [
    country,
    detectedCountry,
    detectedRegion,
    onCountryChange,
    onRegionChange,
    userSelectedCountry,
    userSelectedRegion
  ]);

  const handleSelect = (selectedCountry: string) => {
    if (selectedCountry !== Country.Canada && submitOnSelectionChange) {
      form.submit();
    }
    setCountry(findCountry(getCountryValue(detectedCountry, selectedCountry)));
  };

  return (
    <>
      <CountrySelect
        initialValue={country?.value}
        label={label}
        onClick={handleSelect}
        submit={false}
      >
        {country ? (
          <CountrySelect.Country
            {...country}
            className={showBorders ? `${selected} ${withBorder}` : `${selected} ${withoutBorder}`}
            selected
          >
            <span className={changeText}>
              <Translation {...label} />
            </span>
          </CountrySelect.Country>
        ) : (
          <Button tabIndex={0} autoFocus={isAutofocusEnabled} translation={label} />
        )}
      </CountrySelect>
      {country && country.value === Country.Canada && isAvailableCountryForRegion && (
        <SignupStateSelect
          country={country?.value}
          initialValue={region?.value}
          submit={submitOnSelectionChange}
        >
          <SignupStateSelect.SignupState
            {...region}
            className={showBorders ? `${selected} ${withBorder}` : `${selected} ${withoutBorder}`}
            selected
          >
            <span className={changeText}>
              <Translation {...TXTCLI_Change} />
            </span>
          </SignupStateSelect.SignupState>
        </SignupStateSelect>
      )}
    </>
  );
};

export default SignupCountryField;
